import { CheckCircle2, X } from "lucide-react";

// Big centered success modal shown after a successful donation.
// Replaces the small bottom-right toast so donors get proper affirmation.
export default function DonationSuccessModal({
  open,
  onClose,
  mode,
  amount,
  email,
  currency = "INR",
  isPatron = false,
}) {
  if (!open) return null;

  const isMonthly = mode === "monthly";
  const SYMBOL = { INR: "₹", USD: "$", GBP: "£", EUR: "€" };
  const sym = SYMBOL[currency] || currency + " ";
  const major = amount ? Number(amount) / 100 : 0;
  const formatted =
    currency === "INR"
      ? `${sym}${major.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
      : `${sym}${major.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const amountStr = amount ? `${formatted}${isMonthly ? " /month" : ""}` : "";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="donation-success-title"
      data-testid="donation-success-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 md:px-8"
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-[#1a1a1a]/80 backdrop-blur-sm"
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-xl bg-[#fdfbf7] shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="donation-success-close"
          className="absolute top-4 right-4 text-[#1a1a1a]/50 hover:text-[#1a1a1a] transition-colors"
        >
          <X size={22} strokeWidth={1.5} />
        </button>

        <div className="px-8 md:px-14 pt-14 pb-12 md:pt-16 md:pb-14 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#f9d94b]">
            <CheckCircle2 size={32} strokeWidth={1.6} className="text-[#1a1a1a]" />
          </div>

          <p className="mt-8 font-mono text-[11px] tracking-[0.35em] uppercase text-[#2d4a22]">
            {isPatron
              ? isMonthly
                ? "Welcome, Patron"
                : "Thank you, Patron"
              : isMonthly
                ? "Welcome to the Circle"
                : "Thank you for your kindness"}
          </p>

          <h2
            id="donation-success-title"
            className="mt-5 font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight text-[#1a1a1a]"
          >
            {isPatron ? (
              isMonthly ? (
                <>
                  You are now a <em className="italic">Patron</em>.
                </>
              ) : (
                <>
                  Your patronage
                  <br />
                  has been <em className="italic">received</em>.
                </>
              )
            ) : isMonthly ? (
              <>
                You are now part of the <em className="italic">journey</em>.
              </>
            ) : (
              <>
                Your donation
                <br />
                has been <em className="italic">received</em>.
              </>
            )}
          </h2>

          {amountStr && (
            <p className="mt-6 font-serif text-xl md:text-2xl text-[#1a1a1a]">
              {amountStr}
            </p>
          )}

          <p className="mt-8 font-sans text-[15px] md:text-[16px] leading-relaxed text-[#4a4a4a] max-w-md mx-auto">
            {isPatron ? (
              isMonthly ? (
                <>
                  A confirmation is on its way to{" "}
                  <strong className="text-[#1a1a1a]">{email}</strong>. Every
                  month you&rsquo;ll receive impact updates, exclusive content,
                  and member-only benefits from our creative community. This is
                  a paid digital membership, not a charitable donation, and is
                  not eligible for Indian tax benefits.
                </>
              ) : (
                <>
                  A confirmation is being sent to{" "}
                  <strong className="text-[#1a1a1a]">{email}</strong>. This is a
                  paid contribution supporting our storytelling and impact work,
                  not a charitable donation, and is not eligible for Indian tax
                  benefits.
                </>
              )
            ) : isMonthly ? (
              <>
                A confirmation is on its way to{" "}
                <strong className="text-[#1a1a1a]">{email}</strong>.
                Every month you&rsquo;ll receive impact updates and glimpses of
                the lives you&rsquo;re helping sustain. Your Income Tax
                Exemption certificate will be shared as per the applicable
                provisions of the Income Tax Act.
              </>
            ) : (
              <>
                A confirmation is being sent to{" "}
                <strong className="text-[#1a1a1a]">{email}</strong>.
                Your Income Tax Exemption certificate will be shared as per the
                applicable provisions of the Income Tax Act.
              </>
            )}
          </p>

          <p className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a4a4a]/70">
            If you don&rsquo;t see it, please check your spam folder.
          </p>

          <button
            type="button"
            onClick={onClose}
            data-testid="donation-success-continue"
            className="mt-10 inline-flex items-center gap-3 bg-[#1a1a1a] text-[#fdfbf7] px-8 py-4 text-sm font-sans tracking-[0.15em] uppercase hover:bg-[#2d2d2d] transition-colors"
          >
            Continue
          </button>

          <p className="mt-8 font-serif italic text-lg text-[#4a4a4a]">
            With love and gratitude,
            <br />
            <span className="font-sans not-italic text-[13px] tracking-[0.15em] uppercase text-[#1a1a1a]">
              Stories by Aradhana Foundation for Change ❤️
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
